import { DateOnly } from '@if/core/date'
import {
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { getCreditTestCases } from '@src/api/testcase-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { every } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PRNG } from 'seedrandom'
import seedRandom from 'seedrandom'
import FormDialog from '../Common/Dialog/FormDialog'
import { AddressSchemaType, CustomerSchemaType } from './loan-form-schema'

export interface CreditTestCase {
  id: string
  applicant: ApplicantTestCase
  description: string
}

interface ApplicantTestCase {
  firstName: string
  lastName: string
  middleName: string
  birthDate: DateOnly
  languageId: number
  genderId: number
  currentAddress: AddressSchemaType
}

interface DialogProps {
  onTestCaseSelected: (applicant: CustomerSchemaType) => void
  open: boolean
  onClose: () => void
}

interface TestCaseItemProps {
  onTestCaseSelected: (applicant: ApplicantTestCase) => void
  testcase: CreditTestCase
  selected: boolean
}

const TestCaseItem = ({ onTestCaseSelected, testcase, selected }: TestCaseItemProps) => {
  const { applicant } = testcase

  const primaryText = `${applicant.firstName} ${applicant.lastName} (${applicant.birthDate.toString()})`
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(testcase.applicant)} selected={selected}>
        <ListItemText primary={primaryText} secondary={testcase.description} />
      </ListItemButton>
      <Divider />
    </>
  )
}

const ListCreditTestCaseDialog = ({ open, onClose, onTestCaseSelected }: DialogProps) => {
  const { t } = useTranslation()

  const [selectedApplicant, setSelectedApplicant] = useState<ApplicantTestCase | null>(null)
  const [creditTestCases, setCreditTestCases] = useState<CreditTestCase[]>([])
  const [filteredCreditTestCases, setFilteredCreditTestCases] = useState<CreditTestCase[]>([])
  const [textFilter, setTextFilter] = useState('')
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    if (open && creditTestCases.length === 0) {
      setLoading(true)
      getCreditTestCases()
        .then((values: CreditTestCase[]) => {
          setLoading(false)
          setCreditTestCases(values)
        })
        .catch(reportErrorToConsole)
    }
  }, [open, creditTestCases.length])

  useEffect(() => {
    const filters = textFilter.toLowerCase().split(' ')
    const match = creditTestCases.filter((testcase) => {
      return every(
        filters,
        (f) =>
          testcase.applicant.firstName.toLowerCase().includes(f) ||
          testcase.applicant.lastName.toLowerCase().includes(f) ||
          testcase.description.toLowerCase().includes(f),
      )
    })
    setFilteredCreditTestCases(match)
  }, [textFilter, creditTestCases])

  const onTestCaseClicked = (applicant: ApplicantTestCase) => {
    const random: PRNG = seedRandom(`${applicant.firstName}${applicant.lastName}`) // Weak unique id
    onTestCaseSelected({
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      middleName: applicant.middleName,
      birthDate: new DateOnly(applicant.birthDate),
      languageCode: applicant.languageId,
      genderId: applicant.genderId,
      equifaxNumber: Math.abs(random.int32()).toString(),
      address: applicant.currentAddress,
      alisId: Math.abs(random.int32()),
    })
  }

  return (
    <FormDialog
      open={open}
      maxWidth="lg"
      sx={{ '.MuiDialog-paper': { height: '100%' } }}
      title={
        <Stack spacing={2}>
          <Typography variant="inherit">{t('loan.testCases.title')}</Typography>
          <TextField
            name="filter"
            onChange={(e) => setTextFilter(e.target.value)}
            value={textFilter}
            autoFocus
            label="Filtre"
          />
        </Stack>
      }
      onClose={onClose}
      disabled={selectedApplicant === null}
      dividers
      disableGutterContent
      onConfirm={() => {
        if (selectedApplicant) onTestCaseClicked(selectedApplicant)
      }}
    >
      {isLoading && <LinearProgress />}
      {!isLoading && filteredCreditTestCases.length > 0 && (
        <List>
          {filteredCreditTestCases.map((testcase) => (
            <TestCaseItem
              key={testcase.id}
              testcase={testcase}
              onTestCaseSelected={setSelectedApplicant}
              selected={testcase.applicant === selectedApplicant}
            />
          ))}
        </List>
      )}

      {!isLoading && filteredCreditTestCases.length === 0 && <p>{t('loan.testCases.notFound')}</p>}
    </FormDialog>
  )
}

export default ListCreditTestCaseDialog
